/**
 * The station bands' shader material.
 *
 * Split out of `StationBands.tsx` so the GLSL and the material flags can be
 * asserted in vitest without an r3f harness or a WebGL context. What a unit
 * test CAN prove here is that every attribute the vertex shader declares is
 * one `buildBandGeometry` actually supplies, that every varying is declared on
 * both sides, and that the depth/blend flags are the ones the design calls
 * for. What it CANNOT prove is that the driver compiles it — that needs a real
 * viewer (Task 17's smoke run).
 */
import * as THREE from 'three';

export const BAND_VERTEX_SHADER = /* glsl */ `
attribute vec3 aColor;
attribute vec2 aState;
attribute float aHatch;
attribute vec2 aHatchUv;
uniform mat3 uHatchMatrix;
varying vec3 vColor;
varying vec2 vState;
varying float vHatch;
varying vec2 vHatchUv;
void main() {
  vColor = aColor;
  vState = aState;
  vHatch = aHatch;
  // aHatchUv is (station, offset) in metres, so the stripes cross the BAND at
  // 45 degrees whatever the alignment's bearing. A world-XY UV would run the
  // stripes parallel to a north-east corridor and hide the hatch entirely.
  vHatchUv = (uHatchMatrix * vec3(aHatchUv, 1.0)).xy;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

export const BAND_FRAGMENT_SHADER = /* glsl */ `
uniform sampler2D uHatch;
uniform float uOpacity;
uniform vec3 uHighlight;
varying vec3 vColor;
varying vec2 vState;
varying float vHatch;
varying vec2 vHatchUv;
void main() {
  // vState.x is the brush emphasis (1 inside, 0.35 outside); vState.y is the
  // hover flag. Both arrive as a per-vertex attribute, so changing either is
  // one buffer upload and no geometry rebuild (ruling T14.2).
  vec3 col = mix(vColor, uHighlight, vState.y * 0.35);
  float alpha = uOpacity * vState.x * (1.0 + vState.y * 0.35);
  alpha *= mix(1.0, texture2D(uHatch, vHatchUv).a, vHatch);
  if (alpha < 0.004) discard;
  gl_FragColor = vec4(col, min(alpha, 1.0));
  #include <colorspace_fragment>
}
`;

/**
 * `depthTest` is OFF on purpose. Each band is a FLAT plate at its bin's
 * `zTopMeanM` (spec §4e), so on a crowned carriageway or a cross-sloped
 * corridor the real surface rises through the plate and a depth-tested band
 * comes out half-buried — the one thing most likely to look broken on a real
 * site. The sibling overlay makes the same call for the same reason
 * (`AlignmentOverlay.tsx:199,314,344`). `renderOrder` keeps the bands under
 * the overlay's ticks and walk marker (999), so the centreline still reads
 * on top.
 */
export function createBandMaterial(hatchMap: THREE.Texture): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: {
      uHatch: { value: hatchMap },
      uHatchMatrix: { value: hatchMap.matrix },
      uOpacity: { value: 0.55 },
      uHighlight: { value: new THREE.Color(1, 1, 1) },
    },
    vertexShader: BAND_VERTEX_SHADER,
    fragmentShader: BAND_FRAGMENT_SHADER,
    transparent: true,
    depthWrite: false,
    depthTest: false,
    side: THREE.DoubleSide,
  });
}
